import {
  readWord,
  splitSyllables,
  toneFromMarks,
  writeSyllable,
} from "@kensio/pinyinjs";

import { pinyinRun } from "./fuxi-reading-ink.js";

/**
 * Writing the tone numbers a reader typed as the marks they stand for.
 *
 * What `./fuxi-pinyin.ts` calls on every keystroke. Split from it because that
 * module is about a caret and this one is about the language, the same line
 * `./fuxi-reading-ink.ts` is drawn on next door.
 *
 * **The field holds two notations at once, and that is the whole difficulty.**
 * A reader typing `bing4xing2` has `bìngxing2` in front of them by the time
 * they reach the last digit, half of it marked and half of it numbered. So
 * whatever reads that run has to find the boundary between `bìng` and `xing2`
 * with no 隔音符号 to go on.
 *
 * **A mark on the wrong vowel reads as a syllable.** `readSyllable` is
 * deliberately tolerant of one, which is what lets a reader be understood, and
 * the tolerance costs a cut. `bùan1` comes apart as `bùa` and `n1`, because
 * `bùa` passes for `buà` and `an1` is barred from starting a syllable that no
 * apostrophe precedes. The reader then watches `bù ān` come out as `bùan̄`.
 * Over the 100,032 readings the 词典 draws, typing the numbered form of each
 * one a character at a time left 373 of them wrong, every one a word whose
 * second syllable starts with a vowel.
 *
 * **So the marked half is never read again.** Only the tail with no mark in it
 * goes to `readWord`, and the head in front of it comes back exactly as the
 * reader left it. The boundary is the first place a tail will read as a word,
 * found by trying each in turn from the front. `bìngxing2` offers `ngxing2`
 * and `gxing2` before `xing2`, and the dictionary refuses the first two. All
 * 373 come out right, and so do the other 99,659.
 */

/** Whether a run holds a tone number, which is what asks for it to be marked. */
const numbered = /[1-5]/u;

/**
 * Whether the head in front of a boundary is syllables a reader has finished.
 *
 * An empty head is the ordinary case of a reader starting a word, where the
 * whole run is still in numbers.
 */
function settled(head: string): boolean {
  return head === "" || splitSyllables(head) !== undefined;
}

/**
 * One run with its numbered syllables written in marks.
 *
 * A run the dictionary cannot read as a word is left exactly as it was. `tu1`
 * is a word of one syllable and `xq1` is somebody's typing, and neither this
 * nor the marking has any business correcting the second. A run with no digit
 * in it is left alone as well, `search` finding nothing and the loop running
 * no times. `nv` is a reader halfway through `nv3`, and turning it into `nü`
 * under them is a correction they did not ask for.
 */
function converted(run: string): string | undefined {
  const first = run.search(numbered);

  for (let at = 0; at <= first; at++) {
    const head = run.slice(0, at);
    const typing = run.slice(at);
    const syllables =
      toneFromMarks(typing) === undefined && settled(head)
        ? readWord(typing)
        : undefined;

    if (syllables !== undefined) {
      const written = syllables.map((syllable) =>
        writeSyllable(syllable, "marks"),
      );

      return `${head}${written.join("")}`;
    }
  }

  return undefined;
}

/**
 * The same text with every numbered syllable written in tone marks.
 *
 * @param text - What the reader has typed so far, up to the caret.
 * @returns The same text, with the numbers it can read written as marks.
 */
export function toneMarked(text: string): string {
  return text.replace(pinyinRun, (run) => converted(run) ?? run);
}
